import { CrmPersona } from "components/ui/persona";
import React from "react";
import { useTenantSettings } from "store/selectors/settings";
import { NO_NAME } from "../../util/text";

type ClientPageNameHeaderProps = {
    primaryClientName: string;
    additionalClientName: string;
    bestContactName: string;
    serial?: number;
};

const personaSegment = (name: string): React.ReactNode | null =>
    name && name.trim() !== "" ? <CrmPersona name={name} size="auto" /> : null;

interface NamesHeaderRowProps {
    segments: React.ReactNode[];
    serial: number | null;
}

const NamesHeaderRow: React.FC<NamesHeaderRowProps> = props => {
    const { segments, serial } = props;
    const nameSegments =
        segments.length > 0
            ? segments
            : [
                  <h2 key="no-name" className="twoverride text-sm md:text-lg">
                      {NO_NAME}
                  </h2>,
              ];
    return (
        <div className="flex items-center gap-x-2 md:gap-x-4 min-w-0 flex-1">
            {serial !== null ? (
                <div className="mr-4 md:mr-8 text-gray-500 font-light text-lg md:text-2xl flex-shrink-0">
                    {serial}
                </div>
            ) : null}
            <div className="divide-x divide-solid divide-gray-300 flex items-center -ml-2 md:-ml-4 min-w-0 flex-1">
                {nameSegments.map((x, i) => (
                    <div className="px-2 md:px-4 min-w-0 truncate" key={i}>
                        {x}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const ClientNamesHeader: React.FC<ClientPageNameHeaderProps> = props => {
    const { primaryClientName, additionalClientName, bestContactName, serial } = props;
    const { settings } = useTenantSettings();
    const showClientFirst = settings.showClientFirst;
    const showClientSerial = settings.showClientSerial && !!serial;
    const bestContactSegment = personaSegment(bestContactName);
    const clientNameSegment =
        (primaryClientName && primaryClientName.trim() !== "") ||
        (additionalClientName && additionalClientName.trim() !== "") ? (
            <div className="flex space-x-2 md:space-x-4">
                {personaSegment(primaryClientName)}
                {personaSegment(additionalClientName)}
            </div>
        ) : null;

    const nameSegments = (
        showClientFirst
            ? [clientNameSegment, bestContactSegment]
            : [bestContactSegment, clientNameSegment]
    ).filter(x => !!x);

    return (
        <NamesHeaderRow
            segments={nameSegments}
            serial={showClientSerial ? serial : null}
        />
    );
};

interface HomeCareLeadNamesHeaderProps {
    clientName: string;
}

export const HomeCareLeadNamesHeader: React.FC<HomeCareLeadNamesHeaderProps> = props => {
    const nameSegments = [personaSegment(props.clientName)].filter(x => !!x);

    return <NamesHeaderRow segments={nameSegments} serial={null} />;
};
